import { useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import {
  Controller,
  type FieldValues,
  FormProvider,
  useForm,
} from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import appConfig from '../../../config';
import type { InsetVariableType, ListTotalConfig, Question } from '../../../config/types';
import content from '../../../content';
import { useFormConfig } from '../../../hooks/useFormConfig';
import { useFormData } from '../../../hooks/useFormData';
import type { FetchFormResponse } from '../../../service/types';
import { Button, Query, Status } from '../../../types';
import {
  getConditionalChildRouting,
  hasBlockedSubmitGuardValue,
  isQuestionDisabled,
  renderAnswer,
} from '../../../utils/config-utils';
import { resolveSelectedFormId } from '../../../utils/form-id';
import { getUnsupportedGdadRoleBannerMessage } from '../../../utils/gdad-role-policy';
import { parseSafeHtml } from '../../../utils/safe-html';
import {
  formatTotalValue,
  getListAnswers,
  getListTotals,
  hasValue,
  interpolateQuestionText,
} from '../../../utils/form-utils';
import { getInstancePrefix } from '../../../utils/autofill-math';
import ButtonText from '../../ButtonText';
import Notification from '../../Notification/Notification';
import { renderSummaryRow } from '../../SummaryAnswers/SummaryAnswers';
import Tag from '../../Tag/Tag';
import { P } from '../../ui';
import AutocompleteWrapper from '../Autocomplete/AutocompleteWrapper';
import AutoFillCondition from '../AutoFillCondition';
import AutoFillMath from '../AutoFillMath';
import CheckboxGroup from '../CheckboxGroup/CheckboxGroup';
import { CoreCostDelegatedInput } from '../CoreCostDelegatedInput';
import { CoreCostExternalInput } from '../CoreCostExternalInput';
import { CoreCostSCSInput } from '../CoreCostSCSInput';
import { CoreCostInteralInput } from '../CoreCostInteralInput';
import { CoreCphConsolidatedInput } from '../CoreCphConsolidatedInput';
import { CoreNpsConsolidatedInput } from '../CoreNpsConsolidatedInput';
import { CoreNpsDelegatedInput } from '../CoreNpsDelegatedInput';
import { CoreNpsSCSInput } from '../CoreNpsSCSInput';
import { CoreTtfConsolidatedInput } from '../CoreTtfConsolidatedInput';
import { CoreTthConsolidatedInput } from '../CoreTthConsolidatedInput';
//custom core elements
import { CoreTthDelegatedInput } from '../CoreTthDelegatedInput';
import { CoreTthSCSInput } from '../CoreTthSCSInput';
import { CoreVacConsolidatedInput } from '../CoreVacConsolidatedInput';
import CostInputField from '../CostInputField';
import { DateInputField } from '../DateInputField/DateInputField';
import { DevelopmentProgramme } from '../DevelopmentProgramme';
import { GradeInputField } from '../DateInputField/GradeInputField';
import { GradeInputFieldCoulmnwise } from '../DateInputField/GradeInputFieldCoulmnwise';
import { ShortDateInputField } from '../DateInputField/ShortDateInputField';
import { DisabilityInput } from '../DisabilityInput';
import DynamicGrid from '../DynamicGrid';
import DualInput from '../DualInput';
import { EthnicityInput } from '../EthnicityInput';
import { ExceptionBreakdownInput } from '../ExceptionBreakdownInput';
import Fieldset from '../Fieldset';
import FileUpload from '../FileUpload';
import { GenderInput } from '../GenderInput';
import { InputBreaches } from '../InputBreaches';
import { InputExternalCompetitions } from '../InputExternalCompetitions';
import { InputExternalExemptions } from '../InputExternalExemptions';
import { InputFairAndOpen } from '../InputFairAndOpen';
import { InputRecruitmentCampaigns } from '../InputRecruitmentCampaigns';
import GdadSkillDefinition from '../GdadSkillDefinition';
import GdadProficiencyRating from '../GdadProficiencyRating';
import Inset from '../Inset';
import InstanceInputField from '../InstanceInputField';
import OptionalLabel from '../OptionalLabel';
import PostcodeLookup, { validateManualAddress } from '../PostcodeLookup/PostcodeLookup';
import RadioGroup from '../RadioGroup/RadioGroup';
import { ReportPeriod } from '../ReportPeriod';
import { ReportMonth } from '../ReportMonth';
import RowsWithInputs from '../RowsWithInputs';
import SelectInputField from '../SelectInputField';
import Textarea from '../Textarea';
import TextInputField from '../TextInputField';
import DataLoad from '../DataLoad';
import AthenaStaffLookup from '../AthenaStaffLookup';
import AthenaNominatorLookup from '../AthenaNominatorLookup';
import GdadRoleSelectionInput from '../GdadRoleSelectionInput';
import TwoTextBox from '../TwoTextBox';
import type { Props } from './types';

enum Screen {
  ListPage = 'ListPage',
  QuestionPage = 'QuestionPage',
  ConfirmDeletePage = 'ConfirmDeletePage',
}

const descriptionContainsHtml = (value: string): boolean =>
  /<\s*\/?\s*[a-z][^>]*>/i.test(value);

const parseCurrencyAmount = (value: unknown): number | null => {
  if (typeof value !== 'string' && typeof value !== 'number') return null;

  const normalized = String(value).replace(/[^\d.]/g, '').trim();
  if (!normalized) return null;

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatCurrencyAmount = (amount: number): string => {
  const rounded = Math.round((amount + Number.EPSILON) * 100) / 100;
  return new Intl.NumberFormat('en-GB', {
    minimumFractionDigits: Number.isInteger(rounded) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(rounded);
};

const roundDownToNearestFive = (amount: number): number => Math.floor(amount / 5) * 5;
const roundDownToTwoDecimalPlaces = (amount: number): number =>
  Math.floor((amount + Number.EPSILON) * 100) / 100;

type RewardTeamSplitNotice = {
  message: string;
  note?: string;
};

const getRewardTeamNomineeCount = (
  answers: Record<string, unknown>,
  formConfig: ReturnType<typeof useFormConfig>['formConfig'],
): number => {
  const teamNomineePage = formConfig?.sections
    ?.flatMap((section) => section.subsections)
    .flatMap((subsection) => subsection.pages)
    .find((page) => page.name === 'team-nominee');

  if (!teamNomineePage || teamNomineePage.pageType !== 'list' || !teamNomineePage.questions?.length) {
    return 0;
  }

  const groupedTeamAnswers = getListAnswers(answers, teamNomineePage.questions);
  return Object.values(groupedTeamAnswers).filter((group) =>
    group.some(({ answer }) => hasValue(answer)),
  ).length;
};

const getRewardVoucherMinimumAmount = (
  answers: Record<string, unknown>,
  formConfig: ReturnType<typeof useFormConfig>['formConfig'],
): number => {
  const nominationKind = String(answers['nomination-kind'] || '').trim();
  if (nominationKind !== 'Team') return 10;

  const teamNomineeCount = getRewardTeamNomineeCount(answers, formConfig);
  return Math.max(10, teamNomineeCount * 5);
};

const filterRewardVoucherAmountOptions = (
  options: Question['options'],
  minimumAmount: number,
): Question['options'] => {
  if (!Array.isArray(options)) return options;

  return options
    .filter((option) => {
      if (typeof option !== 'string') return true;
      const amount = parseCurrencyAmount(option);
      return amount === null || amount >= minimumAmount;
    })
    .map((option) => {
      if (
        typeof option !== 'string' &&
        'option' in option &&
        option.option === 'Other' &&
        Array.isArray(option.questions)
      ) {
        return {
          ...option,
          questions: option.questions.map((question) =>
            question.name === 'reward-voucher-other-amount'
              ? {
                  ...question,
                  rules: {
                    ...(question.rules || {}),
                    min: minimumAmount,
                  },
                  errorMessages: {
                    ...(question.errorMessages || {}),
                    min: `Voucher amount must be £${minimumAmount} or more`,
                  },
                }
              : question,
          ),
        };
      }

      return option;
    });
};

const prefixQuestionNames = (questions: Question[], prefix: string): Question[] =>
  questions.map((question) => ({
    ...question,
    name: `${prefix}-${question.name}`,
    options: Array.isArray(question.options)
      ? question.options.map((option) => {
          if (
            typeof option !== 'string' &&
            'questions' in option &&
            Array.isArray(option.questions)
          ) {
            return {
              ...option,
              questions: prefixQuestionNames(option.questions, prefix),
            };
          }
          return option;
        })
      : question.options,
  }));

const renderPageDescription = (description: string | undefined): React.ReactNode =>
  (() => {
    const content = description?.trim() || '';
    if (!content) return null;

    // Render HTML descriptions as a single block so multiline tags are not split into visible text.
    if (descriptionContainsHtml(content)) {
      return (
        <div className="question-page-description" data-testid="question-page-description">
          <div className="gdad-description-block">{parseSafeHtml(content)}</div>
        </div>
      );
    }

    const paragraphs = content
      .split(/\n\s*\n/)
      .map((paragraph) => paragraph.trim())
      .filter(Boolean);

    return (
      <div className="question-page-description" data-testid="question-page-description">
        {paragraphs.map((paragraph) => (
          <P key={paragraph.substring(0, 50)}>{parseSafeHtml(paragraph)}</P>
        ))}
      </div>
    );
  })();

function QuestionPageTemplate({
  config,
  onSave,
  onSaveUnfinished,
  errors,
  disabled,
  onContinue,
  subsectionSlug,
}: Props) {
  const id = useId();
  const { questions = [], pageType } = config;
  const [showSaveButton, setShowSaveButton] = useState<boolean>(true);
  const [listPageContinueErrorMessage, setListPageContinueErrorMessage] = useState('');
  const [screen, setScreen] = useState<string>(
    pageType === 'list' ? Screen.ListPage : Screen.QuestionPage,
  );
  const [currentQuestions, setCurrentQuestions] = useState<Question[]>(questions);
  const { data } = useFormData();
  const { tenant, formConfig, enabledSubsections } = useFormConfig();
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const selectedFormId = resolveSelectedFormId(tenant, location?.search || '');

  const methods = useForm<FetchFormResponse['answers']>({
    ...appConfig.form,
    defaultValues: queryClient.getQueryData<FetchFormResponse>([
      Query.FormData,
      selectedFormId || '',
    ])?.answers,
  });
  const watchedGdadRole = methods.watch('role');
  const selectedGdadRole =
    typeof watchedGdadRole === 'string' && watchedGdadRole.trim().length > 0
      ? watchedGdadRole
      : data?.answers?.role;
  const unsupportedGdadRoleBannerMessage =
    tenant === 'gdad' ? getUnsupportedGdadRoleBannerMessage(selectedGdadRole) : null;

  const listAnswers = data?.answers ?? methods.getValues();
  const listGroupedAnswers = getListAnswers(listAnswers, questions);
  const shouldCheckQueriesContact =
    pageType === 'list' &&
    config.name === 'points-of-contact' &&
    questions.some((q) => q.name === 'contacts-type');
  const hasQueriesContact = Object.values(listGroupedAnswers).some((group) =>
    group.some(({ questionName, answer }) => {
      if (!questionName.endsWith('contacts-type')) return false;
      if (typeof answer === 'string') return answer === 'Point of contact for queries';
      return false;
    }),
  );
  const listPageErrorMessage =
    shouldCheckQueriesContact && !hasQueriesContact
      ? "You must add at least one 'Point of contact for queries'"
      : '';
  const populatedListEntryCount = Object.values(listGroupedAnswers).filter((group) =>
    group.some(({ answer }) => hasValue(answer)),
  ).length;
  const minimumRequiredListItemsErrorMessage =
    pageType === 'list' &&
    config.minimumRequiredListItems &&
    populatedListEntryCount < config.minimumRequiredListItems
      ? config.minimumRequiredListItemsErrorMessage ||
        `You must add at least ${config.minimumRequiredListItems} entries`
      : '';
  const subsectionPages =
    formConfig?.sections
      ?.flatMap((section) => section.subsections)
      .find((subsection) => subsection.slug === subsectionSlug)?.pages || [];
  const enabledSubsectionPages = subsectionSlug
    ? enabledSubsections?.[subsectionSlug]?.pages || subsectionPages
    : subsectionPages;
  const conditionalRouting = getConditionalChildRouting(
    subsectionPages,
    enabledSubsectionPages,
    data || { answers: {}, drafts: {} },
    subsectionSlug || '',
  );
  const listTotalConfig: ListTotalConfig | undefined =
    pageType === 'list' ? config.total : undefined;
  const watchedRewardType = methods.watch('reward-type');
  const watchedRewardVoucherAmount = methods.watch('reward-voucher-amount');
  const watchedRewardVoucherOtherAmount = methods.watch('reward-voucher-other-amount');
  const watchedIybAmount = methods.watch('iyb-amount');
  const mergedAnswers = {
    ...(data?.answers || {}),
    ...(methods.getValues() || {}),
  };
  const visibleQuestions = currentQuestions.filter(
    (question) => !isQuestionDisabled(question, mergedAnswers),
  );
  const rewardVoucherMinimumAmount =
    tenant === 'reward' && config.name === 'reward-details'
      ? getRewardVoucherMinimumAmount(mergedAnswers, formConfig)
      : 10;

  const formErrors = methods.formState.errors;
  const submitCount = methods.formState.submitCount;
  const titleRef = useRef<HTMLHeadingElement>(null);
  const lastAutoScrollSubmitCount = useRef(0);

  const replaceInsetVariables = (
    content: string | undefined,
    variables: Record<string, { type: InsetVariableType; key: string }> | undefined,
  ) => {
    if (!content || !variables || !data?.answers) return content;

    let replacedContent = content;
    for (const [key, { type, key: answerKey }] of Object.entries(variables)) {
      if (type === 'previousAnswer') {
        const answer = Object.keys(data.answers).find((k) => k.endsWith(answerKey));
        if (answer) {
          replacedContent = replacedContent.replace(`\${${key}}`, data.answers[answer]);
        }
      }
    }
    return replacedContent;
  };

  const getRewardTeamSplitNotice = (): RewardTeamSplitNotice | null => {
    if (config.name !== 'reward-details') return null;
    const nominationKind = String(mergedAnswers['nomination-kind'] || '').trim();
    if (nominationKind !== 'Team') return null;
    const teamNomineeCount = getRewardTeamNomineeCount(mergedAnswers, formConfig);

    if (teamNomineeCount < 1) return null;

    let rawAmount: unknown;
    let messagePrefix: string | null = null;
    if (watchedRewardType === 'Reward voucher') {
      rawAmount =
        watchedRewardVoucherAmount === 'Other'
          ? watchedRewardVoucherOtherAmount
          : watchedRewardVoucherAmount;
      messagePrefix = 'Each team member will receive a £';
    } else if (watchedRewardType === 'In-year bonus (IYB)') {
      rawAmount = watchedIybAmount;
      messagePrefix = 'Each team member will receive £';
    } else {
      return null;
    }

    const totalAmount = parseCurrencyAmount(rawAmount);
    if (totalAmount === null) return null;

    if (watchedRewardType === 'In-year bonus (IYB)') {
      const perPersonAmount = roundDownToTwoDecimalPlaces(totalAmount / teamNomineeCount);
      return {
        message: `${messagePrefix}${formatCurrencyAmount(perPersonAmount)} (before tax)`,
      };
    }

    const perPersonAmount = roundDownToNearestFive(totalAmount / teamNomineeCount);
    return {
      message: `${messagePrefix}${formatCurrencyAmount(perPersonAmount)} voucher`,
      note:
        '(Thank you e-vouchers are held in denominations of £5, so the total voucher amount is split per team member with that in mind)',
    };
  };

  useEffect(() => {
    if (tenant !== 'reward' || config.name !== 'reward-details') return;
    if (watchedRewardType !== 'Reward voucher') return;
    if (watchedRewardVoucherAmount === 'Other' || !watchedRewardVoucherAmount) return;

    const selectedAmount = parseCurrencyAmount(watchedRewardVoucherAmount);
    if (selectedAmount !== null && selectedAmount < rewardVoucherMinimumAmount) {
      methods.setValue('reward-voucher-amount', '', {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [
    config.name,
    methods,
    rewardVoucherMinimumAmount,
    tenant,
    watchedRewardType,
    watchedRewardVoucherAmount,
  ]);

  // set props errors on start
  useEffect(() => {
    for (const error of errors || []) {
      methods.setError(error.name, error.error, error.options);
    }
  }, [errors, methods.setError]);

  useEffect(() => {
    titleRef.current?.focus();
  }, [config]);

  useEffect(() => {
    if (screen === Screen.ListPage) {
      setListPageContinueErrorMessage('');
    }
  }, [screen, data?.answers]);

  useEffect(() => {
    if (screen !== Screen.QuestionPage) return;

    const errorKeys = Object.keys(formErrors);

    if (!errorKeys.length) return;
    if (submitCount <= lastAutoScrollSubmitCount.current) return;

    const candidateErrorNames = [
      ...visibleQuestions
        .map((question) => question.name)
        .filter((questionName) => Boolean(formErrors[questionName])),
      ...errorKeys,
    ];
    const uniqueCandidateErrorNames = Array.from(new Set(candidateErrorNames));

    const getTargetForErrorName = (errorName: string): HTMLElement | null => {
      // Prefer scrolling to the fieldset so the question title/legend is visible above the errored control.
      const fieldsetTestId = `question-fieldset-${errorName}`;
      const targetByFieldset = Array.from(
        document.querySelectorAll<HTMLElement>('[data-testid]'),
      ).find((element) => element.getAttribute('data-testid') === fieldsetTestId);
      if (targetByFieldset instanceof HTMLElement) return targetByFieldset;

      const anchorId = `${id}${errorName}`;
      const targetById = document.getElementById(anchorId);
      if (targetById instanceof HTMLElement) return targetById;

      return null;
    };

    let target: HTMLElement | null = null;
    for (const errorName of uniqueCandidateErrorNames) {
      const candidate = getTargetForErrorName(errorName);
      if (candidate) {
        target = candidate;
        break;
      }
    }

    if (!target) {
      const firstVisibleErrorGroup = document.querySelector('.govuk-form-group--error');
      if (firstVisibleErrorGroup instanceof HTMLElement) {
        target = firstVisibleErrorGroup;
      }
    }

    if (target && typeof target.scrollIntoView === 'function') {
      const scrollTarget = target;
      window.requestAnimationFrame(() => {
        scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }

    lastAutoScrollSubmitCount.current = submitCount;
  }, [formErrors, id, screen, submitCount, visibleQuestions]);

  const addQuestion = useCallback(
    (formValues: FieldValues) => {
      // get max id and increment
      const questionIdPrefix =
        Object.keys(formValues)
          .filter((item) => item.endsWith(questions[0].name))
          .map((item) => Number(item.split('-')[0]))
          .reduce((max, current) => Math.max(max, current), 0) + 1;

      setCurrentQuestions(prefixQuestionNames(questions, String(questionIdPrefix)));
      setScreen(Screen.QuestionPage);
    },
    [questions],
  );

  const getCurrentListItemId = useCallback((): string | undefined => {
    if (pageType !== 'list' || currentQuestions.length === 0) return undefined;

    const firstQuestionName = currentQuestions[0]?.name;
    if (firstQuestionName?.includes('-')) {
      const listItemId = firstQuestionName.split('-')[0];
      console.log(
        '[LIST SAVE] Extracted list item ID:',
        listItemId,
        'from question name:',
        firstQuestionName,
      );
      return listItemId;
    }

    console.warn('[LIST SAVE] Could not extract list item ID. Question name:', firstQuestionName);
    return undefined;
  }, [currentQuestions, pageType]);

  useEffect(() => {
    if (pageType === 'list') {
      const formValues = methods.getValues();
      if (Object.entries(getListAnswers(formValues, questions)).length < 1) {
        addQuestion(formValues);
      }
    }
  }, [addQuestion, methods, pageType, questions]);

  // Keep local form values in sync with server data when showing the list view
  useEffect(() => {
    if (data?.answers) {
      if (pageType === 'list' && screen === Screen.ListPage) {
        methods.reset(data.answers);
      }
      if (pageType !== 'list' && screen === Screen.QuestionPage) {
        methods.reset(data.answers);
      }
    }
  }, [pageType, screen, data?.answers, methods]);

  async function handleDelete(data: FieldValues, deletedQuestions: Question[]) {
    const deletedQuestionNames = deletedQuestions.map((q) => q.name);
    const dataToSave = Object.fromEntries(
      Object.entries(data).map(([question, answer]) =>
        deletedQuestionNames.includes(question) ? [question, ''] : [question, answer],
      ),
    );

    // update local form state immediately so the list view uses the new values
    methods.reset(dataToSave);

    // Extract list item ID from the first deleted question name (e.g., "1-contact-name" -> "1")
    const listItemId = deletedQuestions[0]?.name?.split('-')[0];

    // Persist the delete first; navigation (0 items to index) is decided in SubsectionScreen
    await onSave(dataToSave, methods.setError, listItemId);

    await queryClient.invalidateQueries({ queryKey: [Query.FormData, selectedFormId || ''] });
    await queryClient.refetchQueries({ queryKey: [Query.FormData, selectedFormId || ''] });

    setScreen(Screen.ListPage);
  }

  function getQuestionTitle(name: string): string {
    return questions.find((question) => question.name === name)?.title || name;
  }

  function renderTotals(formValues: FieldValues) {
    if (!listTotalConfig || !listTotalConfig.columns?.length) return null;

    const { groupBy, rows, hasEntries } = getListTotals(formValues, questions, listTotalConfig);
    if (!hasEntries) return null;

    return (
      <>
        <h2 className="govuk-heading-m">Totals</h2>
        <table className="govuk-table">
          <thead className="govuk-table__head">
            <tr className="govuk-table__row">
              {groupBy.map((fieldName) => (
                <th className="govuk-table__header" key={`group-header-${fieldName}`}>
                  {getQuestionTitle(fieldName)}
                </th>
              ))}
              {listTotalConfig.columns.map((column) => (
                <th className="govuk-table__header" key={`total-header-${column.field}`}>
                  {column.title}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="govuk-table__body">
            {rows.map((row) => (
              <tr className="govuk-table__row" key={`totals-row-${row.key}`}>
                {groupBy.map((fieldName, index) => (
                  <td className="govuk-table__cell" key={`group-value-${row.key}-${fieldName}`}>
                    {row.groupValues[index]}
                  </td>
                ))}
                {row.totals.map((value, index) => (
                  <td className="govuk-table__cell" key={`total-value-${row.key}-${index}`}>
                    {formatTotalValue(
                      value,
                      questions.find((question) => question.name === listTotalConfig.columns[index].field),
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </>
    );
  }

  // Per-entry status + incremental label (Answer 1/2/3...)
  function renderListSummary(formValues: FieldValues, questions: Question[], listLabel?: string) {
    const groupedAnswers = getListAnswers(formValues, questions);

    console.log('[renderListSummary] Full drafts data:', data?.drafts);
    console.log(
      '[renderListSummary] Drafts for subsection:',
      subsectionSlug,
      '->',
      data?.drafts?.[subsectionSlug || ''],
    );

    const listItemLabel = listLabel || 'Response';

    return (
      <>
        {Object.entries(groupedAnswers).map(([id, answers], idx) => {
          // Check if this specific list item is marked as draft
          // Draft identifiers are in format "page-name:list-item-id" (e.g., "contact-details:1")
          const pageName = config.name;
          const draftIdentifier = `${pageName}:${id}`;

          console.log(
            `[renderListSummary] Checking item ${id}:`,
            'draftIdentifier:',
            draftIdentifier,
            'subsectionSlug:',
            subsectionSlug,
          );

          const isDraft =
            (subsectionSlug && data?.drafts?.[subsectionSlug]?.includes(draftIdentifier)) || false;

          console.log(`[renderListSummary] Item ${id} isDraft:`, isDraft);

          // Completed iff every required question in this entry has a value.
          const isComplete = answers.every(({ question, answer }) =>
            question?.rules?.required === true ? hasValue(answer) : true,
          );

          // Draft status takes precedence over completion status
          const status = isDraft
            ? Status.InProgress
            : isComplete
              ? Status.Completed
              : Status.InProgress;

          return (
            <React.Fragment key={id}>
              <tr className="govuk-table__row">
                <th className="govuk-table__cell" style={{ whiteSpace: 'nowrap' }}>
                  <span>
                    {listItemLabel} {idx + 1}
                  </span>
                  <Tag status={status} style={{ marginLeft: 8 }} />
                </th>
                <td
                  className="govuk-table__cell"
                  colSpan={3}
                  style={{ height: '46px', textAlign: 'right' }}
                >
	                  <ButtonText
	                    type="button"
	                    style={{ fontSize: '19px', marginRight: '20px' }}
		                    onClick={() => {
		                      setListPageContinueErrorMessage('');
		                      setCurrentQuestions(prefixQuestionNames(questions, id));
	                      setScreen(Screen.QuestionPage);
	                    }}
                  >
                    {content.buttonChange}
                  </ButtonText>
	                  <ButtonText
	                    type="button"
	                    style={{ fontSize: '19px' }}
		                    onClick={() => {
		                      setListPageContinueErrorMessage('');
		                      setCurrentQuestions(prefixQuestionNames(questions, id));
	                      setScreen(Screen.ConfirmDeletePage);
	                    }}
                  >
                    {content.buttonRemove}
                  </ButtonText>
                </td>
              </tr>
              {answers.map((answer) => {
                const formattedAnswers = renderAnswer(answer.question, answer.answer);
                return (
                  <React.Fragment key={answer.questionName}>
                    {renderSummaryRow(answer.question, formattedAnswers, formValues)}
                  </React.Fragment>
                );
              })}
            </React.Fragment>
          );
        })}
      </>
    );
  }

  const listSummaryValues = data?.answers ?? methods.getValues();
  const handleSaveAndAddAnother = methods.handleSubmit(async (formValues) => {
    const listItemId = getCurrentListItemId();
    const saveResult = await onSave(formValues, methods.setError, listItemId, {
      mode: 'addAnother',
    });
    if (saveResult === false) return;

    methods.clearErrors();
    setListPageContinueErrorMessage('');
    addQuestion(methods.getValues());
    window.requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  });

  return (
    <>
      {unsupportedGdadRoleBannerMessage && (
        <Notification
          content={unsupportedGdadRoleBannerMessage}
          className="govuk-notification-banner--error"
        />
      )}
      {screen === Screen.ListPage && (
        <FormProvider {...methods}>
          {(listPageContinueErrorMessage || listPageErrorMessage) && (
            <div className="govuk-form-group govuk-form-group--error">
              <p className="govuk-error-message" id={`${id}points-of-contact-queries`}>
                {listPageContinueErrorMessage || listPageErrorMessage}
              </p>
            </div>
          )}
          {renderPageDescription(config.description)}
          {renderTotals(listSummaryValues)}
          <table className="govuk-table">
            <tbody className="govuk-table__body">
              {renderListSummary(listSummaryValues, questions, config.listLabel)}
            </tbody>
          </table>

          <div className="gov-uk-button-group">
	            {!config.hideContinueButton && (
	              <button
	                className="govuk-button save-button"
                type="button"
                name="add"
	                data-module="govuk-button"
	                data-prevent-double-click="true"
	                onClick={() => {
	                  setListPageContinueErrorMessage('');
	                  addQuestion(methods.getValues());
	                }}
	                disabled={disabled}
	              >
	                {content.buttonAddAnother}
	              </button>
	            )}
	            {config.changeTypeButton && conditionalRouting.controllerPageName && (
	              <button
	                className="govuk-button govuk-button--secondary"
	                style={{ marginRight: '15px' }}
	                type="button"
	                name="change-type"
	                data-module="govuk-button"
	                data-prevent-double-click="true"
	                onClick={() => {
	                  setListPageContinueErrorMessage('');
	                  navigate(`../${conditionalRouting.controllerPageName}${location.search || ''}`);
	                }}
	                disabled={disabled}
	              >
                  Change type
	              </button>
	            )}
	            <button
	              className="govuk-button govuk-button--secondary"
	              type="button"
              name="continue"
	              data-module="govuk-button"
	              data-prevent-double-click="true"
	              onClick={() => {
	                const nextErrorMessage =
	                  listPageErrorMessage || minimumRequiredListItemsErrorMessage;
	                if (nextErrorMessage) {
	                  setListPageContinueErrorMessage(nextErrorMessage);
	                  return;
	                }
	                setListPageContinueErrorMessage('');
	                if (onContinue) onContinue();
	              }}
	              disabled={disabled}
            >
              {content.buttonContinue}
            </button>
          </div>
        </FormProvider>
      )}

      {screen === Screen.ConfirmDeletePage && (
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit((data) => handleDelete(data, currentQuestions))}>
            <P>{content.confirmRemoval}</P>

            {currentQuestions.map((question) => (
              <P key={question.name}>
                <b>{question.title}</b>: {methods.getValues(question.name)}
              </P>
            ))}

            <div className="gov-uk-button-group">
              <button
                className="govuk-button govuk-button--warning delete-button"
                type="submit"
                name="delete"
                data-module="govuk-button"
                data-prevent-double-click="true"
                disabled={disabled}
              >
                {content.buttonConfirmRemove}
              </button>
              <button
                className="govuk-button govuk-button--secondary"
                type="button"
                name="cancel"
                data-module="govuk-button"
                data-prevent-double-click="true"
                onClick={() => setScreen(Screen.ListPage)}
                disabled={disabled}
              >
                {content.buttonCancelRemove}
              </button>
            </div>
          </form>
        </FormProvider>
      )}

      {screen === Screen.QuestionPage && (
        <>
          {renderPageDescription(config.description)}
          {tenant === 'gdad' && config.name === 'proficiency-rating' && (
            <GdadProficiencyRating answers={data?.answers || methods.getValues()} />
          )}
          {config.gdadSkillDefinition && (
            <GdadSkillDefinition
              skillName={config.gdadSkillDefinition.skillName}
              skillUrl={config.gdadSkillDefinition.skillUrl}
              level={config.gdadSkillDefinition.level}
              bullets={config.gdadSkillDefinition.bullets}
            />
          )}

          <FormProvider {...methods}>
            <form
              //onSubmit={methods.handleSubmit(async (data) => {
              //  await onSave(data, methods.setError);
              //  if (pageType === 'list') setScreen(Screen.ListPage);
              //})}
              onSubmit={methods.handleSubmit(async (data) => {
                const listItemId = getCurrentListItemId();
                await onSave(data, methods.setError, listItemId);
                if (pageType === 'list') return;
              })}
            >
              {visibleQuestions.map((question) => {
                const errorMessage = methods.formState.errors[question.name];
                const anchorId = `${id}${question.name}`;
                const legendId = `${id}legend`;
                const defaultValue = methods.getValues(question.name);
                const watchedValue = methods.watch(question.name);
                const resolvedQuestionTitle =
                  interpolateQuestionText(question.title, mergedAnswers) || question.title;
                const hintText = interpolateQuestionText(question.hint, mergedAnswers);
                const resolvedQuestion = {
                  ...question,
                  title: resolvedQuestionTitle,
                  hint: hintText,
                };
                const prepopulateValue = data?.prepopulatedPlaceholders?.[question.name];
	                const placeholderOverride =
	                  prepopulateValue !== undefined &&
	                  prepopulateValue !== null &&
	                  String(prepopulateValue).length > 0
	                    ? String(prepopulateValue)
	                    : question.placeholder;
	                const isRewardVoucherAmountQuestion =
	                  tenant === 'reward' &&
	                  config.name === 'reward-details' &&
	                  question.name === 'reward-voucher-amount';
	                const rules = question?.rules || {};
	                const effectiveOptions = isRewardVoucherAmountQuestion
	                  ? filterRewardVoucherAmountOptions(question.options, rewardVoucherMinimumAmount)
	                  : question.options;
	                const effectiveErrorMessages = question.errorMessages;
	                const isAutoMath = !!question.autofillMath;
                const isAutoCondition = !!question.autoFillCondition;
                const hideOptional = !!question.hideOptionalLabel;
                const showRequiredAsterisk =
                  formConfig?.requiredFlag === true && rules?.required === true;
                const rewardTeamSplitNotice =
                  question.name === 'nomination-justification-text'
                    ? getRewardTeamSplitNotice()
                    : null;

                const submitGuardMessage =
                  typeof question.submitGuard?.message === 'string' &&
                  question.submitGuard.message.trim().length > 0 &&
                  hasBlockedSubmitGuardValue(question, {
                    ...(methods.getValues() as any),
                    [question.name]: watchedValue,
                  } as any)
                    ? question.submitGuard.message
                    : null;
                const nomineeFieldPrefix = getInstancePrefix(question.name);
                const nomineeLookupValue = methods.watch(`${nomineeFieldPrefix}nominee-lookup`);
                const isRewardAutofillNomineeField =
                  tenant === 'reward' &&
                  question.readOnly === true &&
                  [
                    'nominee-full-name',
                    'nominee-email',
                    'nominee-staff-number',
                    'nominee-cost-centre',
                    'nominee-grade',
                    'nominee-function',
                    'nominee-line-manager-email',
                  ].some((suffix) => question.name.endsWith(suffix));
                const hasRewardAutofillValue =
                  isRewardAutofillNomineeField &&
                  typeof nomineeLookupValue === 'string' &&
                  nomineeLookupValue.trim().length > 0 &&
                  typeof watchedValue === 'string' &&
                  watchedValue.trim().length > 0;
                if (question.questionType === 'hidden') {
                  return (
                    <input
                      key={question.name}
                      type="hidden"
                      {...methods.register(question.name)}
                    />
                  );
                }

                if (question.questionType === 'content') {
                  return (
                    <div className="govuk-form-group" key={question.name || question.title}>
                      {resolvedQuestion.title ? (
                        <h2 className="govuk-heading-m">{parseSafeHtml(resolvedQuestion.title)}</h2>
                      ) : null}
                      {renderPageDescription(resolvedQuestion.hint)}
                      {renderPageDescription(question.description)}
                    </div>
                  );
                }

                return (
                  <div
                    className={`govuk-form-group ${errorMessage && 'govuk-form-group--error'}`}
                    key={question.name}
                    style={
                      hasRewardAutofillValue
                        ? {
                            borderLeft: '5px solid #00703c',
                            paddingLeft: '15px',
                          }
                        : undefined
                    }
                  >
                    <Fieldset
                      title={
                        <OptionalLabel
                          text={resolvedQuestionTitle}
                          optional={!(rules?.required === true || hideOptional)}
                        />
                      }
                      showRequiredAsterisk={showRequiredAsterisk}
                      hintText={hintText}
                      legendId={legendId}
                      data-testid={`question-fieldset-${question.name}`}
                    >
                      <>
                        {rewardTeamSplitNotice && (
                          <>
                            <p
                              className={`govuk-body govuk-!-font-weight-bold ${
                                rewardTeamSplitNotice.note
                                  ? 'govuk-!-margin-bottom-1'
                                  : 'govuk-!-margin-bottom-3'
                              }`}
                            >
                              {rewardTeamSplitNotice.message}
                            </p>
                            {rewardTeamSplitNotice.note && (
                              <p className="govuk-body govuk-!-margin-top-0 govuk-!-margin-bottom-3">
                                {rewardTeamSplitNotice.note}
                              </p>
                            )}
                            <hr className="govuk-section-break govuk-section-break--m govuk-section-break--visible" />
                          </>
                        )}
                        {question.autofillMath && (
                          <AutoFillMath question={question} answers={data?.answers} />
                        )}
                        {question.autoFillCondition && (
                          <AutoFillCondition question={question} answers={data?.answers} />
                        )}
                        {question.questionType === 'radio' && (
                          <RadioGroup
                            anchorId={anchorId}
                            disabled={disabled || question.readOnly || isAutoCondition}
                            question={question}
                            id={id}
                            showRequiredFlag={formConfig?.requiredFlag}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                            assets={data?.assets}
                          />
                        )}
                        {question.questionType === 'date' && (
                          <DateInputField
                            question={question}
                            anchorId={anchorId}
                            disabled={disabled}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {question.questionType === 'shortDate' && (
                          <ShortDateInputField
                            question={question}
                            anchorId={anchorId}
                            disabled={disabled}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {/* {question.questionType === 'gradeInput' && (
                          <GradeInputField
                            question={question}
                            anchorId={anchorId}
                            disabled={disabled}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )} */}
                        {question.questionType === 'twoTextBox' && (
                          <TwoTextBox
                            title1={question.title}
                            title2="arul-title"
                            name1={question.name}
                            name2={`${question.name}hello`}
                            width="one-quarter"
                            rules1={rules}
                            rules2={rules}
                            defaultValue={defaultValue || ''}
                            errorMessages1={question.errorMessages}
                            errorMessages2={question.errorMessages}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {/* {question.questionType === 'grid' && <Grid5x5 />} */}
                        {question.questionType === 'select' && (
                          <div
                            className={`govuk-form-group ${
                              errorMessage && 'govuk-form-group--error'
                            }`}
                            key={question.name}
                            id={anchorId}
                          >
	                            <SelectInputField
	                              name={question.name}
	                              id={anchorId}
	                              aria-labelledby={legendId}
	                              hintOption={question.hintOption}
	                              options={effectiveOptions || []}
	                              assets={data?.assets}
	                              disabled={disabled || question.readOnly || isAutoCondition}
	                              rules={rules}
	                              title={question.title}
	                              errorMessages={effectiveErrorMessages}
	                              detailsHeading={question.detailsHeading}
	                              detailsContent={question.detailsContent}
                              showRequiredFlag={formConfig?.requiredFlag}
                              defaultValue={
                                typeof question.defaultValue === 'string'
                                  ? question.defaultValue
                                  : undefined
                              }
                            />
                          </div>
                        )}
                        {question.questionType === 'checkbox' && (
                          <CheckboxGroup
                            anchorId={anchorId}
                            disabled={disabled}
                            question={resolvedQuestion}
                            id={id}
                            showRequiredFlag={formConfig?.requiredFlag}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                            assets={data?.assets}
                          />
                        )}
                        {question.questionType === 'textInput' && (
                          <TextInputField
                            aria-labelledby={legendId}
                            id={anchorId}
                            disabled={disabled}
                            title={question.title}
                            name={question.name}
                            approver={question.approver}
                            allowSelfApprover={question.allowSelfApprover}
                            restrictDomains={question.restrictDomains}
                            placeholder={placeholderOverride}
                            wordCount={question.wordCount}
                            characterCount={question.characterCount}
	                            rules={rules}
	                            defaultValue={defaultValue || ''}
	                            errorMessages={effectiveErrorMessages}
	                            detailsHeading={question.detailsHeading}
	                            detailsContent={question.detailsContent}
                            autoFill={question.autoFill}
                            userEmail={question.userEmail}
                            readOnly={question.readOnly || isAutoMath || isAutoCondition}
                            aria-readonly={question.readOnly || isAutoMath || isAutoCondition}
                          />
                        )}
                        {question.questionType === 'textInputSmall' && (
                          <TextInputField
                            aria-labelledby={legendId}
                            id={anchorId}
                            disabled={disabled}
                            title={question.title}
                            name={question.name}
                            approver={question.approver}
                            allowSelfApprover={question.allowSelfApprover}
                            restrictDomains={question.restrictDomains}
                            placeholder={placeholderOverride}
                            wordCount={question.wordCount}
                            characterCount={question.characterCount}
	                            rules={rules}
	                            width="one-third"
	                            defaultValue={defaultValue || ''}
	                            errorMessages={effectiveErrorMessages}
	                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                            autoFill={question.autoFill}
                            userEmail={question.userEmail}
                            readOnly={question.readOnly || isAutoMath || isAutoCondition}
                            aria-readonly={question.readOnly || isAutoMath || isAutoCondition}
                          />
                        )}
                        {question.questionType === 'inset' && (
                          <Inset
                            insetContent={replaceInsetVariables(
                              question.insetContent,
                              question.insetVariables,
                            )}
                            heading={'my heading'}
                          />
                        )}
                        {question.questionType === 'rowsWithInput' && (
                          <RowsWithInputs
                            aria-labelledby={legendId}
                            id={anchorId}
                            // tableData={question.tableData || { headers: [], rows: [] }}
                            disabled={disabled}
                            title={question.title}
                            name={question.name}
                            rules={rules}
                            defaultValue={defaultValue || ''}
                            errorMessages={question.errorMessages}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {question.questionType === 'dualInput' && (
                          <DualInput
                            aria-labelledby={legendId}
                            id={anchorId}
                            disabled={disabled}
                            title={question.title}
                            name={question.name}
                            name1="elan-name"
                            rules={rules}
                            defaultValue={defaultValue || ''}
                            errorMessages={question.errorMessages}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {question.questionType === 'grade' && (
                          <GradeInputField
                            question={question}
                            anchorId={anchorId}
                            disabled={disabled}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {question.questionType === 'gradeColumn' && (
                          <GradeInputFieldCoulmnwise
                            question={question}
                            anchorId={anchorId}
                            disabled={disabled}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {question.questionType === 'recruitmentCampaignInput' && (
                          <InputRecruitmentCampaigns
                            question={question}
                            anchorId={anchorId}
                            disabled={disabled}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {question.questionType === 'ethnicityInput' && (
                          <EthnicityInput
                            question={question}
                            anchorId={anchorId}
                            disabled={disabled}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {question.questionType === 'genderInput' && (
                          <GenderInput
                            question={question}
                            anchorId={anchorId}
                            disabled={disabled}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {question.questionType === 'disabilityInput' && (
                          <DisabilityInput
                            question={question}
                            anchorId={anchorId}
                            disabled={disabled}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {question.questionType === 'exceptionBreakdownInput' && (
                          <ExceptionBreakdownInput
                            question={question}
                            anchorId={anchorId}
                            disabled={disabled}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {question.questionType === 'fairAndOpenInput' && (
                          <InputFairAndOpen
                            question={question}
                            anchorId={anchorId}
                            disabled={disabled}
                            label={question.label}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {question.questionType === 'breachesInput' && (
                          <InputBreaches
                            question={question}
                            anchorId={anchorId}
                            disabled={disabled}
                            label={question.label}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {question.questionType === 'externalExemptionInput' && (
                          <InputExternalExemptions
                            question={question}
                            anchorId={anchorId}
                            disabled={disabled}
                            label={question.label}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {question.questionType === 'internalCompetitionsInput' && (
                          <InputExternalCompetitions
                            question={question}
                            anchorId={anchorId}
                            disabled={disabled}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {question.questionType === 'textarea' && (
                          <Textarea
                            aria-labelledby={legendId}
                            numeric={!!question.rules?.number || undefined}
                            id={anchorId}
                            disabled={disabled}
                            title={question.title}
                            name={question.name}
                            placeholder={placeholderOverride}
                            wordCount={question.wordCount}
                            characterCount={question.characterCount}
                            textRows={question.textRows}
                            rules={rules}
                            defaultValue={defaultValue || ''}
                            errorMessages={question.errorMessages}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {question.questionType === 'reportPeriod' && (
                          <ReportPeriod
                            question={question}
                            anchorId={anchorId}
                            disabled={disabled}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {question.questionType === 'reportMonth' && (
                          <ReportMonth
                            question={question}
                            anchorId={anchorId}
                            disabled={disabled}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {question.questionType === 'developmentProgramme' && (
                          <DevelopmentProgramme
                            question={question}
                            anchorId={anchorId}
                            disabled={disabled}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {question.questionType === 'dataLoad' && question.lookupQuestion && (
                          <DataLoad
                            name={question.name}
                            title={question.title}
                            lookupQuestion={question.lookupQuestion}
                            subsectionSlug={subsectionSlug}
                            pageName={config.name}
                            successMessage={question.successMessage}
                          />
                        )}
                        {question.questionType === 'gdadRoleSelectionInput' && (
                          <GdadRoleSelectionInput
                            question={question}
                            disabled={disabled}
                            anchorId={anchorId}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {question.questionType === 'athenaStaffLookup' && (
                          <AthenaStaffLookup
                            question={question}
                            disabled={disabled}
                            anchorId={anchorId}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {question.questionType === 'athenaNominatorLookup' && (
                          <AthenaNominatorLookup
                            question={question}
                            disabled={disabled}
                            anchorId={anchorId}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {question.questionType === 'addressLookup' && (
                          <Controller
                            name={question.name}
                            control={methods.control}
                            rules={{ validate: validateManualAddress }}
                            render={({ field: { ref: _ref, ...field }, fieldState: { error } }) => (
                              <PostcodeLookup
                                {...field}
                                onValueSelect={(data) => methods.setValue(question.name, data)}
                                onValueChange={methods.setValue}
                                setShowSaveButton={setShowSaveButton}
                                defaultValue={defaultValue}
                                manualAddressError={error}
                                detailsHeading={question.detailsHeading}
                                detailsContent={question.detailsContent}
                              />
                            )}
                          />
                        )}
                        {question.questionType === 'autoComplete' && (
                          <AutocompleteWrapper
                            id={anchorId}
                            items={question.items || []}
                            options={Array.isArray(question.options) ? question.options : []}
                            name={question.name}
                            title={question.title}
                            rules={rules}
                            matchLimit={question.matchLimit}
                            fuzzyMatch={question.fuzzyMatch}
                            placeholder={question.searchPlaceholder || placeholderOverride}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                            showRequiredFlag={formConfig?.requiredFlag}
                          />
                        )}
                        {question.questionType === 'fileUpload' && (
                          <FileUpload
                            id={anchorId}
                            name={question.name}
                            label={question.label}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {question.questionType === 'costInput' && (
                          <CostInputField
                            aria-labelledby={legendId}
                            id={anchorId}
                            disabled={disabled}
                            title={question.title}
                            name={question.name}
                            rules={rules}
                            txt={question.txt || ''}
                            defaultValue={defaultValue || ''}
                            errorMessages={question.errorMessages}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {question.questionType === 'instanceInput' && (
                          <InstanceInputField
                            question={question}
                            aria-labelledby={legendId}
                            id={anchorId}
                            disabled={disabled}
                            title={question.title}
                            name={question.name}
                            rules={rules}
                            txt={question.txt || ''}
                            defaultValue={defaultValue || ''}
                            errorMessages={question.errorMessages}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                            readOnly={isAutoMath}
                            aria-readonly={isAutoMath}
                          />
                        )}
                        {question.questionType === 'coreTthDelegatedInput' && (
                          <CoreTthDelegatedInput
                            question={question}
                            anchorId={anchorId}
                            disabled={disabled}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {question.questionType === 'coreTthSCSInput' && (
                          <CoreTthSCSInput
                            question={question}
                            anchorId={anchorId}
                            disabled={disabled}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {question.questionType === 'coreCostDelegatedInput' && (
                          <CoreCostDelegatedInput
                            question={question}
                            anchorId={anchorId}
                            disabled={disabled}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {question.questionType === 'coreCostInteralInput' && (
                          <CoreCostInteralInput
                            question={question}
                            anchorId={anchorId}
                            disabled={disabled}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {question.questionType === 'coreCostExternalInput' && (
                          <CoreCostExternalInput
                            question={question}
                            anchorId={anchorId}
                            disabled={disabled}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {question.questionType === 'coreCostSCSInput' && (
                          <CoreCostSCSInput
                            question={question}
                            anchorId={anchorId}
                            disabled={disabled}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {question.questionType === 'coreNpsDelegatedInput' && (
                          <CoreNpsDelegatedInput
                            question={question}
                            anchorId={anchorId}
                            disabled={disabled}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {question.questionType === 'coreNpsSCSInput' && (
                          <CoreNpsSCSInput
                            question={question}
                            anchorId={anchorId}
                            disabled={disabled}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {question.questionType === 'CoreNpsConsolidatedInput' && (
                          <CoreNpsConsolidatedInput
                            question={question}
                            disabled={disabled}
                            anchorId={anchorId}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}

                        {question.questionType === 'CoreTthConsolidatedInput' && (
                          <CoreTthConsolidatedInput
                            question={question}
                            disabled={disabled}
                            anchorId={anchorId}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}

                        {question.questionType === 'CoreTtfConsolidatedInput' && (
                          <CoreTtfConsolidatedInput
                            question={question}
                            disabled={disabled}
                            anchorId={anchorId}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}

                        {question.questionType === 'CoreCphConsolidatedInput' && (
                          <CoreCphConsolidatedInput
                            question={question}
                            disabled={disabled}
                            anchorId={anchorId}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}

                        {question.questionType === 'CoreVacConsolidatedInput' && (
                          <CoreVacConsolidatedInput
                            question={question}
                            disabled={disabled}
                            anchorId={anchorId}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}

                        {question.questionType === 'dynamicGrid' && (
                          <DynamicGrid
                            question={question}
                            disabled={disabled}
                            anchorId={anchorId}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}

                        {/* Submit guards block submission, but we still allow save-and-continue.
                            Show the guard message inline when a blocked value is selected. */}
                        {submitGuardMessage && (
                          <p
                            className="govuk-error-message govuk-!-margin-top-2 govuk-!-margin-bottom-0"
                            data-testid={`submit-guard-${question.name}`}
                          >
                            {submitGuardMessage}
                          </p>
                        )}
                      </>
                    </Fieldset>
                  </div>
                );
              })}

              <div className="gov-uk-button-group">
                {showSaveButton && (
                  <button
                    className="govuk-button save-button"
                    type="submit"
                    name={Button.Save}
                    data-module="govuk-button"
                    data-prevent-double-click="true"
                    disabled={disabled}
                  >
                    {content.buttonSave}
                  </button>
                )}
                {pageType === 'list' && config.addButton && (
                  <button
                    className="govuk-button save-add-another-button"
                    type="button"
                    name="save-and-add-another"
                    data-module="govuk-button"
                    data-prevent-double-click="true"
                    onClick={handleSaveAndAddAnother}
                    disabled={disabled}
                  >
                    {content.buttonSaveAddAnother}
                  </button>
                )}
	                <button
	                  className="govuk-button govuk-button--secondary"
	                  type="button"
	                  name={Button.SaveUnfinished}
                  data-module="govuk-button"
                  data-prevent-double-click="true"
                  onClick={() => {
                    methods.clearErrors();
                    // Extract list item ID from question names for list pages
                    let listItemId: string | undefined;
                    if (pageType === 'list' && currentQuestions.length > 0) {
                      const firstQuestionName = currentQuestions[0]?.name;
                      if (firstQuestionName?.includes('-')) {
                        listItemId = firstQuestionName.split('-')[0];
                        console.log(
                          '[LIST DRAFT] Extracted list item ID:',
                          listItemId,
                          'from question name:',
                          firstQuestionName,
                        );
                      } else {
                        console.warn(
                          '[LIST DRAFT] Could not extract list item ID. Question name:',
                          firstQuestionName,
                        );
                      }
                    } else {
                      console.log(
                        '[DRAFT] Not a list page or no questions. PageType:',
                        pageType,
                        'Questions:',
                        currentQuestions.length,
                      );
                    }
                    onSaveUnfinished(methods.getValues(), listItemId);
                  }}
                  disabled={disabled}
	                >
	                  {content.buttonSaveUnfinished}
	                </button>
		                {config.changeTypeButton && conditionalRouting.controllerPageName && (
		                  <button
		                    className="govuk-button govuk-button--secondary"
		                    style={{ marginLeft: '15px' }}
		                    type="button"
		                    name="change-type"
		                    data-module="govuk-button"
		                    data-prevent-double-click="true"
	                    onClick={() => {
	                      methods.clearErrors();
	                      navigate(`../${conditionalRouting.controllerPageName}${location.search || ''}`);
	                    }}
	                    disabled={disabled}
	                  >
                    Change type
	                  </button>
	                )}
	              </div>
            </form>
          </FormProvider>
        </>
      )}
    </>
  );
}

export default QuestionPageTemplate;
